<template>
  <div class="multiselect">
    <div
      class="multiselect__input-container"
      id="multiselect-input"
      @click="toggleDropdown"
      role="combobox"
      :aria-expanded="isDropdownOpen"
      aria-haspopup="listbox"
      tabindex="0"
      @keydown.enter="toggleDropdown"
      @keydown.space="toggleDropdown"
    >
      <div class="multiselect__input-container-placeholder-wrapper">
        <span class="multiselect__input-container-placeholder">{{ placeholder }}</span>
        <span v-if="!isDropdownOpen && selectedNames" class="multiselect__input-container-selected-names">{{
          selectedNames
        }}</span>
      </div>
      <span class="multiselect__input-container-chevron" aria-hidden="true">
        <svg :class="['multiselect__input-container-chevron-arrow', { 'is-active': isDropdownOpen }]">
          <use xlink:href="#icon-prev"></use>
        </svg>
      </span>
    </div>
    <div class="multiselect__dropdown" v-if="isDropdownOpen">
      <div class="multiselect__dropdown-item" v-for="item in items" :key="item.id">
        <div class="multiselect__dropdown-item-checkbox-container">
          <label class="multiselect__dropdown-item-checkbox-label" :for="'checkbox-' + item.id">{{ item.name }}</label>
          <input
            type="checkbox"
            :id="'checkbox-' + item.id"
            :value="item.id"
            :name="item.name"
            :checked="selectedOptions.includes(item.id)"
            @change="handleCheckboxChange(item.id, $event.target.checked)"
            class="multiselect__dropdown-item-checkbox"
            :aria-checked="selectedOptions.includes(item.id)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, toRefs, computed } from 'vue';

  const props = defineProps({
    items: {
      type: Array,
      required: true,
    },
    placeholder: {
      type: String,
      default: 'Select options',
    },
    isExpanded: {
      type: Boolean,
      default: false,
    },
    selectedValues: {
      type: Array,
      default: [],
      validator: (value) => {
        // Check if every item in the array is a string or number (ID)
        return value.every((item) => typeof item === 'string' || typeof item === 'number');
      },
    },
  });

  const { isExpanded, selectedValues } = toRefs(props);

  const emit = defineEmits(['update-selected-value']);

  const isDropdownOpen = ref(isExpanded.value);
  const selectedOptions = ref(selectedValues);

  const getSelectedNamesUI = () => {
    return props.items
      .filter((item) => selectedOptions.value.includes(item.id))
      .map((item) => item.name)
      .join(', ');
  };

  const selectedNames = ref(getSelectedNamesUI());

  const toggleDropdown = () => {
    isDropdownOpen.value = !isDropdownOpen.value;
  };

  const handleCheckboxChange = (id, isChecked) => {
    if (isChecked) {
      // Add the selected value if it's not already included
      if (!selectedOptions.value.includes(id)) {
        selectedOptions.value.push(id);
      }
    } else {
      // Remove the unselected value
      const index = selectedOptions.value.indexOf(id);
      if (index !== -1) {
        selectedOptions.value.splice(index, 1);
      }
    }

    selectedNames.value = getSelectedNamesUI();
    emit('update-selected-value', selectedOptions.value);
  };
</script>
